import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { map, debounceTime, tap, catchError, shareReplay } from 'rxjs/operators';
import { ResponseSearchAuctions, Vehicle } from '../model/vehicle';
import { Observable, throwError } from 'rxjs';
import { of } from 'rxjs';
import { Params } from '@angular/router';
import { Banner, OemDealer } from '../model/oem-dealer';
import { City } from '../model/city';
import { SlugifyPipe } from 'ngx-pipes';
import { environment as env } from 'src/environments/environment';
import { PageControl } from '../model/page-control';
import { IdName } from './oem-dealer.service';
import { EvalDealerPersonalizationConfig } from '@app/views/public/eval-dealer-personalization-model';
import { EvaluationVehicle } from '@app-model/evaluation';
import { LayoutVars } from '@app/store/session/session.store';
import { DefaultMessage } from '@app/views/private/admin/services/user-manager.service';
import { DealerDetails } from '@app/views/private/admin/model/admin-models';
import { SessionService } from '@app/store/session/session.service';
import { SendLeadRequest } from '@app-model/auction-lead';
import { Shelf } from '../model/shelf';
import { DefaultStatus } from '@app-model/login';


@Injectable({
  providedIn: 'root',
})
export class OpenService {
  layoutVars: LayoutVars;
  isLayoutVarsLoaded: boolean;
  private layoutVarsRequest: Observable<LayoutVars>;

  constructor(private http: HttpClient, private sessionService: SessionService) { }

  searchAuctions(filter: VehicleFilter): Observable<ResponseSearchAuctions> {

    return this.http
      .post(`${environment.api}/open/searchAuctions`, filter)
      .pipe(
        debounceTime(100),
        map((response: ResponseSearchAuctions) => {
          const data: Vehicle[] = [];
          response.data?.forEach((d) =>
            data.push(Object.assign(new Vehicle(), d))
          );
          response.data = data;
          return response;
        })
      );
  }

  searchChangeAuctionConditionsVehicles(
    filter: VehicleFilter
  ): Observable<SearchChangeAuctionConditionsVehiclesResponse> {
    return this.http
      .post(`${environment.api}/open/searchChangeAuctionConditionsVehicles`, filter)
      .pipe(map((response: SearchChangeAuctionConditionsVehiclesResponse) => response));
  }

  searchOemDealersByName(query: string, onlyWithOffers = true, isFastQuote = true): Observable<OemDealer[]> {
    const data = { query, onlyWithOffers: onlyWithOffers + '', isFastQuote: isFastQuote + '' };
    return this.http
      .get(`${environment.api}/open/oemDealerOptions`, { params: data })
      .pipe(map((response: OemDealer[]) => response));
  }

  searchCitiesByName(
    query: string,
    filter?: VehicleFilter | any,
    withUf?: boolean,
    isFastQuote?: boolean
  ): Observable<City[]> {
    const data = { query };
    withUf = withUf == undefined ? false : withUf;
    isFastQuote = isFastQuote == undefined ? false : isFastQuote;

    if (filter) {
      return this.http
        .post(`${environment.api}/open/cityOptions?withUf=${withUf}&isFastQuote=${isFastQuote}&query=${query}`, filter)
        .pipe(map((response: City[]) => response));
    } else {
      return this.http
        .get(`${environment.api}/open/cityOptions`, { params: data })
        .pipe(map((response: City[]) => response));
    }
  }

  getFilterOptions(
    filter: VehicleFilter
  ): Observable<FilterOptions> {
    return this.http
      .post(`${environment.api}/open/filterOptions`, filter)
      .pipe(map((response: FilterOptions) => response));
  }

  getHomeFilterOptions(
    filter: VehicleFilter
  ): Observable<FilterOptions> {
    return this.http
      .post(`${environment.api}/open/homeFilterOptions`, filter)
      .pipe(map((response: FilterOptions) => response));
  }

  getCityUf(
    withUf: boolean,
    query: string,
    filter: VehicleFilter
  ): Observable<any> {
    return this.http
      .post(`${environment.api}/open/cityOptions?withUf=${withUf}&query=${query}`, filter)
      .pipe(map((response: any) => response));
  }

  getOemDealer(oemDealerId: string): Observable<OemDealer> {
    return this.http
      .get(`${environment.api}/open/oemDealer/${oemDealerId}`)
      .pipe(map((response: OemDealer) => response));
  }

  getAuctionDetails(
    vehicleId: string,
    ownsVehicle: boolean
  ): Observable<Vehicle> {
    const queryParams: any = {};
    if (ownsVehicle) {
      queryParams.myVehicle = 'true';
    }
    return this.http
      .get(`${environment.api}/open/auction/${vehicleId}`, {
        params: queryParams,
      })
      .pipe(map((response: Vehicle) => Object.assign(new Vehicle(), response)));
  }

  async buildVehicleUrl(vehicle: Vehicle): Promise<string> {
    const url = await this.getUrl() || env.url;
    return `${url}/compartilhar/arrematar/veiculos/${this.getVehicleSlug(vehicle)}/${vehicle.id
      }`;
  }

  async getUrl(): Promise<string> {
    try {
      const layoutVars = await this.getLayoutVars().toPromise();
      if (layoutVars && layoutVars.alternativeDomain) {
        if (!layoutVars.alternativeDomain.startsWith('https://')) {
          return 'https://' + layoutVars.alternativeDomain;
        }

        return layoutVars.alternativeDomain;
      }
    } catch (e) {
      console.error(e);
    }

    return env.url;
  }

  getVehicleSlug(vehicle: Vehicle | EvaluationVehicle): string {
    const slugifyPipe = new SlugifyPipe();
    const vehicleDescription = `${vehicle.make} ${vehicle.trim} ${vehicle.manufactureYear} ${vehicle.modelYear}`;
    return slugifyPipe.transform(vehicleDescription);
  }

  getOemDealerSlug(oemDealerName: string): string {
    const slugifyPipe = new SlugifyPipe();
    return slugifyPipe.transform(oemDealerName);
  }

  getThumbImageUrl(imgUrl: string): string {
    if (imgUrl.indexOf('img.autoarremate.') >= 0) {
      const lastDot = imgUrl.lastIndexOf('.');
      return (
        imgUrl.substring(0, lastDot) + '_thumb' + imgUrl.substring(lastDot)
      );
    } else {
      return imgUrl;
    }
  }

  getCity(id: string): Observable<City> {
    return this.http
      .get(`${environment.api}/open/city/${id}`)
      .pipe(map((response: City) => response));
  }

  getMakes(ids: string[]): Observable<MakeModel[]> {
    return this.http
      .post(`${environment.api}/open/makes`, ids)
      .pipe(map((response: MakeModel[]) => response));
  }

  getMakesByName(query: string): Observable<MakeModel[]> {
    const data = { query };

    return this.http
      .get(`${environment.api}/open/makeOptions`, { params: data })
      .pipe(map((response: MakeModel[]) => response));
  }

  getMakesBySegment(segment: string): Observable<IdName[]> {
    return this.http
      .get(`${environment.api}/open/makes/${segment}`)
      .pipe(map((response: IdName[]) => response));
  }

  getModels(ids: string[]): Observable<Model[]> {
    return this.http
      .post(`${environment.api}/open/models`, ids)
      .pipe(map((response: Model[]) => response));
  }

  getModelsByMake(makeIds: string[]): Observable<Model[]> {
    return this.http
      .post(`${environment.api}/open/modelsByMakes`, makeIds)
      .pipe(map((response: Model[]) => response));
  }

  getEquipments(ids: string[]): Observable<IdName[]> {
    return this.http
      .post(`${environment.api}/open/equipments`, ids)
      .pipe(map((response: IdName[]) => response));
  }

  downloadCSV(filter: VehicleFilter): Observable<any> {
    return this.http.post(`${environment.api}/open/downloadCSV`, filter, {
      headers: new HttpHeaders({
        Accept: 'application/csv',
      }),
      responseType: 'blob' as 'json'
    });
  }

  getDealerPersonalizationConfig(oemDealerId: string, evalDealerId: string, sellerId: string): Observable<EvalDealerPersonalizationConfig> {
    let param;
    if (oemDealerId) {
      param = "oemDealerId=" + oemDealerId;
    } else if (evalDealerId) {
      param = "evalDealerId=" + evalDealerId;
    } else if (sellerId) {
      param = "sellerId=" + sellerId;
    }
    return this.http.get<EvalDealerPersonalizationConfig>(
      `${environment.api}/open/temp/evalDealerPersonalizationConfig?${param}`);
  }

  getLayoutVars(): Observable<LayoutVars> {
    if (this.isLayoutVarsLoaded) {
      return of(this.layoutVars);
    } else {
      if (!this.layoutVarsRequest) {
        this.layoutVarsRequest = this.http.get<LayoutVars>(`${environment.api}/open/personalization`)
          .pipe(
            tap(result => {
              this.isLayoutVarsLoaded = true;
              this.layoutVars = result;
            }),
            catchError(err => {
              this.layoutVarsRequest = undefined;
              return throwError(() => err);
            }),
            shareReplay(1)
          );
      }

      return this.layoutVarsRequest;
    }
  }

  checkNewDealerCpfCnpj(cpfCnpj: string | number): Observable<DefaultMessage> {
    return this.http.post<DefaultMessage>(`${environment.api}/open/checkNewDealerCpfCnpj`, { cnpj: cpfCnpj });
  }

  validateCnpj(request: { cpf: string, cnpj: string }): Observable<DefaultMessage> {
    return this.http.post<DefaultMessage>(`${environment.api}/login/validate`, request);
  }

  createDealer(dealer: DealerDetails): Observable<CreateDealerResponse> {
    return this.http.post<CreateDealerResponse>(`${environment.api}/open/createMinimalDealer`, dealer);
  }

  createDealerAlternative(dealer: DealerDetails): Observable<CreateDealerResponse> {
    return this.http.post<CreateDealerResponse>(`${environment.api}/open/createDealer`, dealer);
  }

  getOemDealersCarousel(): Observable<OemDealerCarousel[]> {
    return this.http.get<OemDealerCarousel[]>(`${environment.api}/open/oemDealersCarousel`);
  }

  sendLead(request: SendLeadRequest): Observable<DefaultMessage> {
    return this.http.post<DefaultMessage>(`${environment.api}/open/sendLead`, request);
  }

  getBanners(): Observable<Banner[]> {
    return this.http.get<Banner[]>(`${environment.api}/open/getBanners`);
  }

  getShelves(): Observable<Shelf[]> {
    return this.http.get(`${environment.api}/open/getShelves`)
      .pipe(map((response: Shelf[]) => response.map(r => {
        r.fastQuoteFilter = Object.assign(new VehicleFilter, r.fastQuoteFilter);
        r.vehicleFilter = Object.assign(new VehicleFilter, r.vehicleFilter);
        return r;
      })));
  }

  getBasicVehicleInfo(vehicleId: string, isFastQuote: boolean): Observable<BasicVehicleInfo> {
    return this.http.get<BasicVehicleInfo>(`${environment.api}/open/basicVehicleDetails/${vehicleId}?isFastQuote=${isFastQuote}`);
  }

}

export class OemDealerCarousel {
  id: string;
  name: string;
  logo: string;
  score: number;
  redirectToFastQuote: boolean;
}


/* export class Shelf {
  id: string;
  name: string;
  iconClass: string;
  iconContent: string;
  onlyAdmin: boolean;
  type: 'BANNER' | 'CAROUSEL' | 'GRID' | 'HTML' | 'VEHICLE' | 'FAST_QUOTE' | 'OEM_DEALER';
  vehicleFilter: VehicleFilter;
  fastQuoteFilter: VehicleFilter;
  targets: ('MOBILE' | 'DESKTOP')[];
  htmlContent?: string;
  images?: ImageItem[];
  gridConfig?: GridConfig;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  inactive?: boolean;
  title: string;
  order: number;
} */

export class CreateDealerResponse {
  success: boolean;
  errorCode: string;
  autoApproved?: boolean;
}

export interface FilterOptions {
  reportOptions: IdName[];
  dossierOptions: number;
  extraKeyOptions: number;
  carManualOptions: number;
  exclusiveGroups: IdName[];
  makeModels: MakeModel[];
  locations: IdName[];
  minYear: number;
  dealTypeOptions: Record<string, number>;
  fuelOptions: number[];
  transmissionOptions: number[]
  minKm: number,
  maxKm: number,
  minFipe: number,
  maxFipe: number,
  oemDealers: IdName[]
  totalElements?: number;
  segmentOptions?: IdName[];
  auctionLastPublishedBy?: IdName[];
}

export class SelectableOption<T> {
  option: T;
  quantity: number;
  selected: boolean;
}

export interface MakeModel {
  id: string;
  name: string;
  imgUrl: string;
  segment: string;
  models: Model[];
  type: string;
  quantity?: number;
}

export interface Model {
  id: string;
  name: string;
  makeId: string;
  makeName: string;
  type: string;
}

export enum PhotoOptions {
  ALL = 'Todos',
  WITH_PHOTOS = 'Somente com fotos',
  WITHOUT_PHOTOS = 'Somente sem fotos',
}

export const OBJECT_KEY_NAMES = [
  'transmissions',
  'fuels',
  'ufs',
  'cityIds',
  'cityNames',
  'ufsNames',
  'oemDealerIds',
  'equipments',
  'modelIds',
  'makeIds',
  'statuses',
  'expandedStatuses',
  'reportStatuses',
  'segments',
  'oemDealerTypes',
  'reasonsDeletion',
  'dealTypes',
];
export const NUMBER_KEY_NAMES = [
  'cityRadius',
  'modelYearStart',
  'modelYearEnd',
  'priceStart',
  'priceEnd',
  'kmStart',
  'kmEnd',
  'page',
  'fipeDiffEnd',
  'fipeDiffStart',
];
export const BOOLEAN_KEY_NAMES = [
  'onlyFavorites',
  'onlyWithMyBids',
  'boughtByMe',
  'myVehicles',
  'withPhotos',
  'withReport',
  'extraKey',
  'carManual',
  'almostNew',
  'used',
  'withDossier',
  'onlyWithFinalBid',
  'gridMode',
];
export const IGNORED_KEY_NAMES = ['pageControl', 'refresh', 'gridMode'];
export const PAGE_CONTROL_KEY_NAMES = ['page', 'sortField', 'sortDirection'];

export class VehicleFilter {
  boughtByMe?: boolean;
  carManual?: boolean;
  auctionLastPublishedBy?: string;
  cityId?: string;
  cityIds?: string[];
  cityNames?: string[];
  cityRadius?: number;
  locationId?: string;
  extraKey?: boolean;
  expandedStatuses?: string[];
  fuels?: string[];
  fuelOptions?: number[];
  kmEnd?: number;
  kmStart?: number;
  makeIds?: string[];
  modelIds?: string[];
  modelYearEnd?: number;
  modelYearStart?: number;
  myVehicles?: boolean;
  oemDealerId?: string;
  oemDealerTypes?: string[];
  onlyFavorites?: boolean;
  onlyWithMyBids?: boolean;
  onlyWithFinalBid?: boolean;
  pageControl: PageControl;
  priceEnd?: number;
  priceStart?: number;
  almostNew?: boolean;
  used?: boolean;
  withDossier?: boolean;
  withReport?: boolean;
  reportStatuses?: string[];
  excludeDamaged?: boolean;
  searchQuery?: string;
  segments?: string[];
  statuses?: string[];
  transmissions?: string[];
  equipments?: string[];
  withPhotos?: boolean;
  dealersExclusiveGroups: string[]
  minKm?: number;
  maxKm?: number;

  fipeDiffEnd?: number;
  fipeDiffStart?: number;
  gridMode?: boolean = true;
  uf?: string;
  ufs?: string[];
  ufsNames?: string[];
  reasonsDeletion?: string[];
  targets?: string[];
  firstPublishedAtIni?: string;
  oemDealerIds?: string[];
  dealTypes?: string[];
  createdByReportTypes?: string[];


  toParamMap(): Params {
    const params: Params = {};
    Object.keys(this).forEach((k) => {
      let value: any;
      if (OBJECT_KEY_NAMES.indexOf(k) >= 0) {
        value = JSON.stringify(this[k]);
      } else if (IGNORED_KEY_NAMES.indexOf(k) < 0) {
        value = this[k];
      }
      params[k] = value;
    });

    if (this.pageControl.page) {
      params.page = this.pageControl.page;
    }
    params.sortField = this.pageControl.sortField;
    params.sortDirection = this.pageControl.sortDirection;
    return params;
  }

  updateFromParamMap(params: Params): void {
    Object.keys(params).forEach((k) => {
      let value: any;
      if (OBJECT_KEY_NAMES.indexOf(k) >= 0) {
        value = JSON.parse(params[k]);
      } else if (IGNORED_KEY_NAMES.indexOf(k) < 0) {
        value = params[k];
      }

      if (NUMBER_KEY_NAMES.indexOf(k) >= 0) {
        value = Number(value);
        if (value === Number.NaN) {
          value = undefined;
        }
      } else if (BOOLEAN_KEY_NAMES.indexOf(k) >= 0) {
        value = JSON.parse(value);
      }

      if (value !== undefined) {
        if (PAGE_CONTROL_KEY_NAMES.indexOf(k) >= 0) {
          if (k === 'page') {
            value--;
          }
          this.pageControl[k] = value;
        } else {
          this[k] = value;
        }
      }
    });
  }

}

export class InitVehicleFilter {
  boughtByMe?: boolean;
  carManual?: boolean;
  auctionLastPublishedBy?: string;
  cityId?: string;
  cityIds?: string[];
  cityNames?: string[];
  cityRadius?: number;
  locationId?: string;
  extraKey?: boolean;
  expandedStatuses?: string[];
  fuels?: string[];
  kmEnd?: number;
  kmStart?: number;
  makeIds?: string[];
  modelIds?: string[];
  modelYearEnd?: number;
  modelYearStart?: number;
  myVehicles?: boolean;
  oemDealerId?: string;
  oemDealerTypes?: string[];
  onlyFavorites?: boolean;
  onlyWithMyBids?: boolean;
  onlyWithFinalBid?: boolean;
  pageControl: PageControl;
  priceEnd?: number;
  priceStart?: number;
  almostNew?: boolean;
  used?: boolean;
  withDossier?: boolean;
  withReport?: boolean;
  reportStatuses?: string[];
  searchQuery?: string;
  segments?: string[];
  statuses?: string[];
  transmissions?: string[];
  equipments?: string[];
  withPhotos?: boolean;
  dealersExclusiveGroups: string[]

  fipeDiffEnd?: number;
  fipeDiffStart?: number;
  gridMode?: boolean = true;
  uf?: string;
  ufs?: string[];
  ufsNames?: string[];
  reasonsDeletion?: string[];
  targets?: string[];
  firstPublishedAtIni?: string;
  oemDealers?: string[]

  toParamMap(): Params {
    const params: Params = {};
    Object.keys(this).forEach((k) => {
      let value: any;
      if (OBJECT_KEY_NAMES.indexOf(k) >= 0) {
        value = JSON.stringify(this[k]);
      } else if (IGNORED_KEY_NAMES.indexOf(k) < 0) {
        value = this[k];
      }
      params[k] = value;
    });
    if (this.pageControl.page) {
      params.page = this.pageControl.page;
    }
    params.sortField = this.pageControl.sortField;
    params.sortDirection = this.pageControl.sortDirection;
    return params;
  }

  updateFromParamMap(params: Params): void {
    Object.keys(params).forEach((k) => {

      let value: any;
      if (OBJECT_KEY_NAMES.indexOf(k) >= 0) {
        value = JSON.parse(params[k]);
      } else if (IGNORED_KEY_NAMES.indexOf(k) < 0) {
        value = params[k];
      }

      if (NUMBER_KEY_NAMES.indexOf(k) >= 0) {
        value = Number(value);
        if (value === Number.NaN) {
          value = undefined;
        }
      } else if (BOOLEAN_KEY_NAMES.indexOf(k) >= 0) {
        value = JSON.parse(value);
      }

      if (value !== undefined) {
        if (PAGE_CONTROL_KEY_NAMES.indexOf(k) >= 0) {
          if (k === 'page') {
            value--;
          }
          this.pageControl[k] = value;
        } else {
          this[k] = value;
        }
      }
    });
  }

}

export class BasicVehicleInfo {
  make: string;
  model: string;
  trim: string;
  modelYear: number;
  manufactureYear: number;
  color: string;
  imgUrl: string;
  oemDealerName: string;
  reportStatus: string;
  fipePrice: number;
  km: number;
  city: string;
  uf: string;
  price: number;
}


export interface ChangeAuctionConditionsVehicleItem {
  vehicleId?: string;
  oemDealerId?: string;
  imgUrls?: string[];
  numberPlate?: string;
  modelYear?: number;
  km?: number;
  make?: string;
  model?: string;
  trim?: string;
  status?: string;
  locationId?: string;
  target?: string;
  dealersExclusiveGroups?: string[];
  bidOption?: string;
  ignorePersonalizedOffers?: boolean | null;
  conditionalOffer?: boolean | null;
  allowLeadGeneration?: boolean | null;
  autoRenewal?: boolean | null;
  initialBid?: number;
  bidStep?: number;
  finishValue?: number;
  minPersonalizedOfferValue?: number;
  fipePrice?: number;
  minSellPrice?: number;
  costPrice?: number;
}

export interface SearchChangeAuctionConditionsVehiclesResponse {
  actualPage?: number;
  data?: ChangeAuctionConditionsVehicleItem[];
  numberOfPages?: number;
  totalElements?: number;
}